import { useCallback, useState } from "react";

import { Alert2, Cancel, InformationCircle } from "@/assets/icons";
import { Card } from "@/atoms";
import { AlfredActionsButtons } from "@/components";
import { AlfredInsightDataInterface } from "@/interfaces";
import { generateValidClassNameHandler } from "@/lib/utils";
import { InsightSeverityType } from "@/types";
import { Button } from "@/ui/button";

const getSeverityIcon = (severity: InsightSeverityType) => {
    switch (severity) {
    case "error":
        return <Alert2 className="size-5 text-red-500" />;
    case "warning":
        return <Alert2 className="size-5 text-amber-500" />;
    case "info":
    default:
        return <InformationCircle className="size-5 text-blue-500" />;
    }
};

const getSeverityBorderClass = (severity: InsightSeverityType) => {
    switch (severity) {
    case "error":
        return "border-l-4 border-l-red-500";
    case "warning":
        return "border-l-4 border-l-amber-500";
    case "info":
    default:
        return "border-l-4 border-l-blue-500";
    }
};

const getSeverityBgClass = (severity: InsightSeverityType) => {
    switch (severity) {
    case "error":
        return "bg-red-50";
    case "warning":
        return "bg-amber-50";
    case "info":
    default:
        return "bg-blue-50";
    }
};

export const AlfredInsightCard = ({
    className,
    data,
    onAction,
    onDismiss,
} : {
    className?: string,
    data: AlfredInsightDataInterface,
    onAction?: (actionId: string) => void, // eslint-disable-line
    onDismiss?: () => void,
}) => {
    const [isDismissed, setIsDismissed] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const handleDismiss = useCallback(
        () => {
            setIsDismissed(true);
            if (onDismiss) onDismiss();
        },
        [onDismiss],
    );

    if (isDismissed) return null;

    const hasDetails = data.details && data.details.length > 0;
    const hasActions = data.actions && data.actions.length > 0;

    const headerContent = (
        <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
                {getSeverityIcon(data.severity)}
                <span className="text-sm font-medium">{data.title}</span>
            </div>
            <Button
                className="size-6 !p-0"
                size="icon"
                variant="ghost"
                onClick={handleDismiss}
            >
                <Cancel className="size-4" />
            </Button>
        </div>
    );

    const cardContent = (
        <>
            <p className="text-sm text-gray-700">{data.message}</p>

            {hasDetails && (
                <div className="mt-2">
                    <Button
                        className="!h-auto !p-0 text-xs"
                        size="sm"
                        variant="ghost"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        {isExpanded ? "Hide details" : `Show details (${data.details!.length})`}
                    </Button>

                    {isExpanded && (
                        <ul className="mt-2 space-y-1">
                            {data.details!.map((detail, idx) => (
                                <li
                                    className="flex items-center gap-2 text-xs text-gray-600"
                                    key={idx} // eslint-disable-line
                                >
                                    <span className="size-1 rounded-full bg-gray-400" />
                                    {detail}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {/* Action buttons as part of content (Option 3) */}
            {hasActions && (
                <div className="mt-3 border-t pt-3">
                    <AlfredActionsButtons
                        actions={data.actions!}
                        onAction={onAction}
                    />
                </div>
            )}
        </>
    );

    return (
        <Card
            content={cardContent}
            contentClassName="pb-3 pt-0"
            headerClassName="pb-2"
            otherHeaderData={headerContent}
            className={generateValidClassNameHandler(
                "w-full shadow-sm",
                getSeverityBorderClass(data.severity),
                getSeverityBgClass(data.severity),
                className,
            )}
        />
    );
};
